<template>
  <section class="experience-listing">
    <Wrap>
      <div
        v-if="showSkeletons"
        class="list-wrap"
      >
        <ExpCardSkeleton
          v-for="i in skeletonsCount"
          :key="`exp-card-skeleton-${i + 1}`"
        />
      </div>
      <div
        v-else
        class="list-wrap"
      >
        <ExpCard
          v-for="exp in experiences"
          :key="exp.exp.id"
          :exp="exp.exp"
          :show-location="showLocation"
          :is-in-favorites="exp.favorite"
          :sogu-page="eventsPrefix"
          :tooltip-teleport-target="modalTarget"
          class="exp-list-item-wrapper"
          target-blank
          @toggle-favorite="toggleFavorite(exp)"
          @click="handleExpClick(exp.exp)"
        />
      </div>
    </Wrap>
  </section>

  <div
    v-if="moreButton"
    class="exp-button"
  >
    <UiButtonV3
      data-test="article-exp-btn-show-all"
      class="btn"
      :variant="UiButtonVariant.OUTLINE"
      :color-mode="UiButtonColorMode.NEUTRAL"
      :text="moreButtonText"
      :href="moreButtonLink"
      @click.prevent="handleButtonClick"
    />
  </div>
</template>

<script lang="ts" setup>
  import { ExpCard, ExpCardSkeleton, type ExpCardProps } from '@experience/exp-card';
  import { UiButtonV3, UiButtonVariant, UiButtonColorMode } from '@experience/vue3-kit';
  import { storeToRefs } from 'pinia';
  import { computed, inject } from 'vue';

  import { useExperienceEntity, type Experience, type ExperienceWithFavorite } from '@/entities/experience';
  import Wrap from '@/shared/ui/Wrap.vue';

  interface Props {
    /**
     * Количество скелетонов.
     *
     * @default 3
     */
    skeletonsCount?: number;

    /**
     * Отображать кнопку "показать еще".
     *
     * @default false
     */
    moreButton?: boolean;

    /**
     * Текст кнопки "показать еще".
     *
     * @default ""
     */
    moreButtonText?: string;

    /**
     * Ссылка, указываемая в кнопке "показать еще".
     */
    moreButtonLink?: string;

    /**
     * Префикс для аналитических событий карточки.
     */
    analyticEventsPrefix: string;

    /**
     * Флаг загрузки сопутствующих данных страницы.
     */
    generalPageDataLoading?: boolean;

    /**
     * Флаг отображения локации
     */
    showLocation?: boolean;

    /**
     * geo объект
     */
    geoSubject?: {
      id: number;
      name: string;
    };

    /**
     * geo тип
     */
    geoType?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    skeletonsCount: 3,
    moreButton: false,
    moreButtonText: '',
    generalPageDataLoading: false,
  });

  const emit = defineEmits<{
    /**
     * Событие нажатия кнопки "еще".
     */
    'load-more': [];

    /**
     * Событие клика по карточке экскурсии.
     */
    'card-click': [exp: Experience];

    /**
     * Событие добавления экскурсии в избранное.
     */
    'set-favorite': [id: number];

    /**
     * Событие удаления экскурсии из избранного.
     */
    'unset-favorite': [id: number];
  }>();

  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
  const modalTarget = (shadowRoot?.querySelector('.modal-target') ?? undefined) as HTMLElement | undefined;

  // TODO: заменить тип на ExpEventPage и компонента карточки, когда там появятся нужные варианты страниц
  const eventsPrefix = computed(() => props.analyticEventsPrefix as ExpCardProps['soguPage']);

  const experienceEntity = useExperienceEntity();

  const { experiencesWithFavorite: experiences } = storeToRefs(experienceEntity);

  const showSkeletons = computed(
    () => experienceEntity.loading || props.generalPageDataLoading || experienceEntity.loadingFailed,
  );

  const handleButtonClick = () => emit('load-more');

  const toggleFavorite = (exp: ExperienceWithFavorite) => {
    const { id } = exp.exp;

    if (exp.favorite) {
      emit('set-favorite', id);
      experienceEntity.unsetFavorite(id);
    } else {
      emit('unset-favorite', id);
      experienceEntity.setFavorite(id);
    }
  };

  const handleExpClick = (exp: Experience) => emit('card-click', exp);
</script>

<style lang="scss" scoped>
  .experience-listing {
    position: relative;
  }

  .list-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    @supports (display: grid) {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 40px 24px;

      @include desktop-xs-listing {
        grid-template-columns: repeat(auto-fit, minmax(300px, 0.333fr));
      }

      @include tablet {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include mobile {
        grid-template-columns: minmax(0, 1fr);
        gap: 26px 24px;
      }
    }
  }

  .exp-button {
    display: flex;
    justify-content: center;
    margin: 56px 0 0;

    @include tablet {
      margin: 28px 0 0;
    }
  }
</style>
